import {
  OFFLINE_TOAST_MESSAGE,
  TOAST_ACTION_DURATION_MS,
  TOAST_DURATION_MS,
  type ToastType,
} from './catalog.js';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
  durationMs?: number;
  dismissible?: boolean;
}

export interface ToastMessage {
  id: string;
  type: ToastType;
  message: string;
  action?: ToastAction;
  durationMs: number;
  dismissible: boolean;
  createdAt: number;
}

const listeners = new Set<(toast: ToastMessage) => void>();
const dismissListeners = new Set<(id: string) => void>();

function createToast(type: ToastType, message: string, options?: ToastOptions): ToastMessage {
  const hasAction = Boolean(options?.action);
  return {
    id: crypto.randomUUID(),
    type,
    message,
    action: options?.action,
    durationMs: options?.durationMs ?? (hasAction ? TOAST_ACTION_DURATION_MS : TOAST_DURATION_MS),
    dismissible: options?.dismissible ?? true,
    createdAt: Date.now(),
  };
}

function emit(toast: ToastMessage): string {
  listeners.forEach((listener) => listener(toast));
  return toast.id;
}

const shownToastKeys = new Set<string>();

export function showToast(type: ToastType, message: string, options?: ToastOptions & { dedupeKey?: string }): string {
  if (options?.dedupeKey) {
    if (shownToastKeys.has(options.dedupeKey)) return '';
    shownToastKeys.add(options.dedupeKey);
  }
  return emit(createToast(type, message, options));
}

export function showRetryToast(message: string, onRetry: () => void): string {
  return showToast('error', message, {
    action: { label: 'Try again', onClick: onRetry },
  });
}

export function showOfflineToast(message: string = OFFLINE_TOAST_MESSAGE): string {
  return showToast('offline', message, {
    durationMs: TOAST_ACTION_DURATION_MS,
    dismissible: true,
  });
}

export function showOnlineRecoveryToast(message: string = 'Back online.'): string {
  return showToast('info', message);
}

export function dismissToast(id: string): void {
  dismissListeners.forEach((listener) => listener(id));
}

export function subscribeToasts(listener: (toast: ToastMessage) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function subscribeToastDismiss(listener: (id: string) => void): () => void {
  dismissListeners.add(listener);
  return () => dismissListeners.delete(listener);
}
